import { useEffect, useRef, useState } from 'react';
import { AlertCircle, ListPlus, Loader2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { listsApi } from '../../lib/api';
import type { List } from '../../types/list';

interface Props {
  onPick: (listId: string) => void;
  label?: string;
  disabled?: boolean;
  /** Controlled-open: when provided, the parent owns the open state and the trigger is hidden. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AddToListMenu({ onPick, label = 'Add to list', disabled, open: openProp, onOpenChange }: Props) {
  const isControlled = openProp !== undefined;
  const [openUncontrolled, setOpenUncontrolled] = useState(false);
  const open = isControlled ? openProp : openUncontrolled;
  const setOpen = (next: boolean) => {
    if (!isControlled) setOpenUncontrolled(next);
    onOpenChange?.(next);
  };
  const [lists, setLists] = useState<List[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inline "New list…" composer
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingNew, setSavingNew] = useState(false);
  const newInputRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fetchLists = async () => {
    setLoading(true);
    setError(null);
    try {
      const { lists: fetched } = await listsApi.list('mine', 1, 100);
      setLists(fetched.filter((l) => l.kind === 'MANUAL' && !l.isSystem));
    } catch {
      setError('Could not load your lists.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    if (disabled) return;
    const next = !open;
    setOpen(next);
    if (next) {
      setCreating(false);
      setNewName('');
      void fetchLists();
    }
  };

  useEffect(() => {
    if (creating) newInputRef.current?.focus();
  }, [creating]);

  // Controlled-open: fetch lists whenever the parent opens us.
  useEffect(() => {
    if (isControlled && open) {
      setCreating(false);
      setNewName('');
      void fetchLists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, open]);

  const pick = (listId: string) => {
    setOpen(false);
    setCreating(false);
    setNewName('');
    onPick(listId);
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed || savingNew) return;
    setSavingNew(true);
    try {
      const { list } = await listsApi.create({ name: trimmed });
      pick(list.id);
    } catch {
      toast.error('Could not create list');
    } finally {
      setSavingNew(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {/* Trigger — hidden in controlled mode (parent owns the open state). */}
      {!isControlled && (
        <button
          type="button"
          className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
          onClick={handleOpen}
          disabled={disabled}
        >
          <ListPlus size={15} />
          {label}
        </button>
      )}

      {/* Dropdown */}
      {open && (
        <div className={`absolute z-30 card w-72 max-h-80 overflow-auto flex flex-col shadow-xl ${isControlled ? 'right-0 bottom-full mb-1' : 'left-0 mt-1'}`}>
          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading lists…
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="px-4 py-5 text-center">
              <AlertCircle className="mx-auto mb-2 h-5 w-5 text-gray-400" />
              <p className="text-sm text-gray-600 dark:text-gray-300">{error}</p>
              <button
                type="button"
                onClick={() => void fetchLists()}
                className="btn-secondary mt-2 text-sm"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* New list… row / inline composer */}
              {creating ? (
                <div className="border-b border-gray-100 dark:border-gray-700/60 px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <input
                      ref={newInputRef}
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void handleCreate();
                        if (e.key === 'Escape') {
                          setCreating(false);
                          setNewName('');
                        }
                      }}
                      placeholder="List name"
                      className="input text-sm"
                      disabled={savingNew}
                    />
                    <button
                      type="button"
                      onClick={() => void handleCreate()}
                      disabled={!newName.trim() || savingNew}
                      className="btn-primary flex-shrink-0 text-sm disabled:opacity-50"
                    >
                      {savingNew ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        'Create'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCreating(false);
                        setNewName('');
                      }}
                      disabled={savingNew}
                      className="flex-shrink-0 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="flex w-full items-center gap-2.5 border-b border-gray-100 dark:border-gray-700/60 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30"
                >
                  <span className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-lime-100 dark:bg-lime-900/30">
                    <Plus className="h-3.5 w-3.5 text-lime-700 dark:text-lime-400" strokeWidth={2.4} />
                  </span>
                  <span className="text-sm font-semibold text-lime-800 dark:text-lime-300">
                    New list…
                  </span>
                </button>
              )}

              {/* Manual list rows */}
              {lists.length === 0 && !creating ? (
                <div className="px-4 py-5 text-center text-sm text-gray-500 dark:text-gray-400">
                  No lists yet — create one above.
                </div>
              ) : (
                lists.map((list) => (
                  <button
                    key={list.id}
                    type="button"
                    onClick={() => pick(list.id)}
                    className="flex w-full items-center gap-2.5 border-b border-gray-100 dark:border-gray-700/60 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30 last:border-b-0"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {list.name}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                        {list.count ?? 0} items
                      </span>
                    </span>
                    <span className="flex-none rounded-full bg-lime-100 px-2 py-0.5 text-[10px] font-bold tracking-wide text-lime-800 dark:bg-lime-900/30 dark:text-lime-300">
                      MANUAL
                    </span>
                  </button>
                ))
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
